import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReportGenerator = () => {
  const [reportConfig, setReportConfig] = useState({
    reportType: 'summary',
    dateRange: 'last7days',
    customStartDate: '',
    customEndDate: '',
    includeMetrics: {
      callVolume: true,
      agentPerformance: true,
      customerSatisfaction: true,
      serviceLevel: true,
      resolution: false,
      revenue: false
    },
    format: 'pdf',
    schedule: 'none',
    recipients: ''
  });

  const reportTypes = [
    { value: 'summary', label: 'Özet Rapor' },
    { value: 'detailed', label: 'Detaylı Analiz' },
    { value: 'agent', label: 'Temsilci Performansı' },
    { value: 'customer', label: 'Müşteri Analizi' },
    { value: 'operational', label: 'Operasyonel Rapor' }
  ];

  const dateRanges = [
    { value: 'today', label: 'Bugün' },
    { value: 'yesterday', label: 'Dün' },
    { value: 'last7days', label: 'Son 7 Gün' },
    { value: 'last30days', label: 'Son 30 Gün' },
    { value: 'thisMonth', label: 'Bu Ay' },
    { value: 'lastMonth', label: 'Geçen Ay' },
    { value: 'custom', label: 'Özel Tarih Aralığı' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel (XLSX)' },
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' }
  ];

  const scheduleOptions = [
    { value: 'none', label: 'Tek Seferlik' },
    { value: 'daily', label: 'Günlük' },
    { value: 'weekly', label: 'Haftalık' },
    { value: 'monthly', label: 'Aylık' }
  ];

  const handleMetricChange = (metric, checked) => {
    setReportConfig(prev => ({
      ...prev,
      includeMetrics: {
        ...prev?.includeMetrics,
        [metric]: checked
      }
    }));
  };

  const handleGenerateReport = () => {
    console.log('Generating report with config:', reportConfig);
    // Simulate report generation
    alert('Rapor oluşturuluyor... İndirme bağlantısı e-posta ile gönderilecek.');
  };

  const savedReports = [
    {
      id: 1,
      name: 'Haftalık Performans Raporu',
      type: 'Özet Rapor',
      date: '2025-11-06',
      size: '2.4 MB',
      format: 'PDF'
    },
    {
      id: 2,
      name: 'Ekim Ayı Detaylı Analiz',
      type: 'Detaylı Analiz',
      date: '2025-11-01',
      size: '5.8 MB',
      format: 'Excel'
    },
    {
      id: 3,
      name: 'Temsilci Performans Karşılaştırması',
      type: 'Temsilci Performansı',
      date: '2025-10-30',
      size: '1.9 MB',
      format: 'PDF'
    }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Report Configuration */}
      <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Rapor Oluşturucu</h3>
            <p className="text-sm text-slate-600">Özelleştirilmiş raporlar oluşturun ve planlayın</p>
          </div>
          <Icon name="FileText" size={24} color="var(--color-primary)" />
        </div>

        <div className="space-y-6">
          {/* Report Type */}
          <div>
            <Select
              label="Rapor Türü"
              options={reportTypes}
              value={reportConfig?.reportType}
              onChange={(value) => setReportConfig(prev => ({ ...prev, reportType: value }))}
            />
          </div>

          {/* Date Range */}
          <div>
            <Select
              label="Tarih Aralığı"
              options={dateRanges}
              value={reportConfig?.dateRange}
              onChange={(value) => setReportConfig(prev => ({ ...prev, dateRange: value }))}
            />
            
            {reportConfig?.dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Input
                  label="Başlangıç Tarihi"
                  type="date"
                  value={reportConfig?.customStartDate}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, customStartDate: e?.target?.value }))}
                />
                <Input
                  label="Bitiş Tarihi"
                  type="date"
                  value={reportConfig?.customEndDate}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, customEndDate: e?.target?.value }))}
                />
              </div>
            )}
          </div>

          {/* Metrics Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-900 mb-3">
              Dahil Edilecek Metrikler
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Checkbox
                label="Çağrı Hacmi"
                checked={reportConfig?.includeMetrics?.callVolume}
                onChange={(e) => handleMetricChange('callVolume', e?.target?.checked)}
              />
              <Checkbox
                label="Temsilci Performansı"
                checked={reportConfig?.includeMetrics?.agentPerformance}
                onChange={(e) => handleMetricChange('agentPerformance', e?.target?.checked)}
              />
              <Checkbox
                label="Müşteri Memnuniyeti"
                checked={reportConfig?.includeMetrics?.customerSatisfaction}
                onChange={(e) => handleMetricChange('customerSatisfaction', e?.target?.checked)}
              />
              <Checkbox
                label="Hizmet Seviyesi"
                checked={reportConfig?.includeMetrics?.serviceLevel}
                onChange={(e) => handleMetricChange('serviceLevel', e?.target?.checked)}
              />
              <Checkbox
                label="Çözüm Oranları"
                checked={reportConfig?.includeMetrics?.resolution}
                onChange={(e) => handleMetricChange('resolution', e?.target?.checked)}
              />
              <Checkbox
                label="Gelir Analizi"
                checked={reportConfig?.includeMetrics?.revenue}
                onChange={(e) => handleMetricChange('revenue', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Format and Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Dosya Formatı"
              options={formatOptions}
              value={reportConfig?.format}
              onChange={(value) => setReportConfig(prev => ({ ...prev, format: value }))}
            />
            <Select
              label="Planlama"
              options={scheduleOptions}
              value={reportConfig?.schedule}
              onChange={(value) => setReportConfig(prev => ({ ...prev, schedule: value }))}
            />
          </div>

          {/* Recipients */}
          {reportConfig?.schedule !== 'none' && (
            <Input
              label="E-posta Alıcıları"
              type="email"
              placeholder="email1@example.com, email2@example.com"
              description="Virgülle ayırarak birden fazla e-posta adresi girebilirsiniz"
              value={reportConfig?.recipients}
              onChange={(e) => setReportConfig(prev => ({ ...prev, recipients: e?.target?.value }))}
            />
          )}

          {/* Generate Button */}
          <div className="flex items-center space-x-3 pt-4 border-t border-slate-200">
            <Button
              variant="default"
              onClick={handleGenerateReport}
              iconName="Download"
              iconPosition="left"
              className="flex-1"
            >
              Rapor Oluştur
            </Button>
            <Button
              variant="outline"
              iconName="Save"
              iconPosition="left"
            >
              Şablon Kaydet
            </Button>
          </div>
        </div>
      </div>
      {/* Saved Reports */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Kaydedilen Raporlar</h3>
            <p className="text-sm text-slate-600">Son oluşturulan raporlar</p>
          </div>
          <button className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
            <Icon name="MoreVertical" size={16} />
          </button>
        </div>

        <div className="space-y-3">
          {savedReports?.map((report) => (
            <div key={report?.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-medium text-slate-900 line-clamp-2">
                  {report?.name}
                </h4>
                <button className="p-1 text-slate-400 hover:text-slate-600 transition-colors">
                  <Icon name="Download" size={14} />
                </button>
              </div>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-600">{report?.type}</p>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{report?.date}</span>
                  <span>{report?.size}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="FileText" size={12} color="var(--color-text-secondary)" />
                  <span className="text-xs text-slate-500">{report?.format}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-slate-200">
          <Button
            variant="outline"
            size="sm"
            iconName="FolderOpen"
            iconPosition="left"
            className="w-full"
          >
            Tüm Raporları Görüntüle
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;